import { ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import { Search } from '@/components/common/Search';
import { Label, LABEL_TYPE } from '@/components/generic';
import { PAGE_SIZE } from '@/constants';
import { AuthContext } from '@/contexts';
import { COLORS, PERMISSION_TYPE } from '@/enums';
import { ArrowDownBlackIcon, ArrowUpIcon } from '@/resources';
import { BuildingsIcon, XIcon } from '@/resources/icons/sidebar';
import { useGetAllOrganizations } from '@/services';
import { useFilterStore } from '@/store';
import { Organization, SelectOptionProps } from '@/types';

const Menu = (props: React.HTMLProps<HTMLDivElement>) => (
  <div
    className='absolute left-0 top-full z-[9999] mt-2 max-h-[300px] w-[210px] rounded-lg bg-primary-gray shadow-lg md:left-full md:top-0 md:ml-5 md:mt-0 md:w-[200px]'
    {...props}
  />
);

const Blanket = (props: React.HTMLProps<HTMLDivElement>) => (
  <div className='fixed inset-0 z-[9998]' {...props} />
);

const CustomDropdown = ({
  children,
  isOpen,
  target,
  onClose
}: {
  children?: ReactNode;
  readonly isOpen: boolean;
  readonly target: ReactNode;
  readonly onClose: () => void;
}) => (
  <div className='relative'>
    {target}
    {isOpen ? <Menu>{children}</Menu> : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
);

export const OrganisationSelect = () => {
  const [t] = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<SelectOptionProps | null>(null);
  const [searchOrganization, setSearchOrganization] = useState<string>('');

  const { permissions: userPermissions, currentUserData: currentUser } = useContext(AuthContext);

  const hasPermissionViewOrganizations = userPermissions.includes(
    PERMISSION_TYPE.VIEW_ORGANIZATIONS
  );

  const {
    data: allOrganizations,
    fetchNextPage,
    hasNextPage
  } = useGetAllOrganizations(
    PAGE_SIZE,
    searchOrganization,
    undefined,
    hasPermissionViewOrganizations
  );

  const { setOrganizationFilter } = useFilterStore();

  const getOrganizationOptions = () => {
    const allOptions = allOrganizations?.pages.flatMap((page) =>
      (page.data?.items ?? []).map((organization: Organization) => ({
        value: organization.id,
        label: organization.name
      }))
    ) as SelectOptionProps[];

    if (searchOrganization.trim()) {
      return (
        allOptions?.filter((option) =>
          option.label.toLowerCase().includes(searchOrganization.toLowerCase())
        ) || []
      );
    }

    return allOptions || [];
  };

  const options: SelectOptionProps[] =
    currentUser?.organizations && currentUser.organizations.length >= 2
      ? currentUser?.organizations?.map((orgs: Organization) => ({
          value: orgs.id,
          label: orgs.name
        }))
      : getOrganizationOptions();

  const filteredOptions = searchOrganization.trim()
    ? options.filter((option: SelectOptionProps) =>
        option.label.toLowerCase().includes(searchOrganization.toLowerCase())
      )
    : options;

  return (
    <div className='mx-4 w-[210px] md:w-52'>
      <CustomDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        target={
          <button
            onClick={() => setIsOpen((prev) => !prev)}
            className='flex h-10 w-full items-center justify-between rounded-lg px-3 py-2 text-left'
            style={{
              backgroundColor: COLORS.PRIMARY_BLUE,
              color: COLORS.PRIMARY_WHITE
            }}>
            <div className='flex min-w-0 flex-1 items-center text-sm'>
              {!selectedValue && <BuildingsIcon className='mr-3 size-5 shrink-0 text-white' />}
              <div className='flex min-w-0 flex-1 items-center gap-2'>
                <Label
                  text={selectedValue ? selectedValue.label : t('organizations')}
                  type={LABEL_TYPE.PARAGRAPH1}
                  className={'w-[calc(100%-2rem)] truncate !text-sm text-white'}
                />
                {selectedValue && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedValue(null);
                      setOrganizationFilter(null);
                    }}
                    className='absolute right-6 shrink-0 rounded p-1'>
                    <XIcon className='size-3 fill-white' />
                  </button>
                )}
              </div>
            </div>
            <span className='absolute right-2 top-1/2 -translate-y-1/2'>
              {isOpen ? (
                <ArrowUpIcon className='size-4 -rotate-90 fill-primary-white' />
              ) : (
                <ArrowDownBlackIcon className='size-4 -rotate-90 fill-primary-white' />
              )}
            </span>
          </button>
        }>
        <div>
          <div className='rounded-lg border-b border-gray-200 bg-primary-gray px-3 py-2'>
            <Search callback={(query) => setSearchOrganization(query)} isSidebar />
          </div>
          <div className='flex max-h-52 flex-col gap-1 overflow-y-auto p-2 scrollbar'>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option: SelectOptionProps) => (
                <div
                  key={String(option.value)}
                  onClick={() => {
                    setSelectedValue(option);
                    setOrganizationFilter(option);
                    setIsOpen(false);
                  }}
                  className={clsx(
                    'cursor-pointer rounded-md bg-primary-gray px-3 py-2 text-sm text-secondary-steel-blue hover:bg-secondary-light-gray hover:text-accent-dark-gray',
                    selectedValue?.value === option.value &&
                      '!bg-secondary-light-gray font-semibold !text-primary-blue'
                  )}
                  style={{
                    fontWeight: selectedValue?.value === option.value ? '600' : '400'
                  }}>
                  {option.label}
                </div>
              ))
            ) : (
              <div className='px-3 py-2 text-sm text-secondary-steel-blue'>{t('noResults')}</div>
            )}
            {hasNextPage && (
              <div className='cursor-pointer px-3 py-2' onClick={() => fetchNextPage?.()} />
            )}
          </div>
        </div>
      </CustomDropdown>
    </div>
  );
};
